import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import axios from 'axios';
import { PlayIcon, PauseIcon, MusicIcon, NextIcon, PrevIcon, LinkIcon, DownloadIcon } from './Icons';
import { useUser } from '../contexts/UserContext';
import { API_URL } from '../utils/constants';
import { formatDuration } from '../utils/formatters';
import { getFullUrl } from '../utils/api';
import { downloadTrack } from '../utils/download';
import { pickDominantBand, directionForBand } from '../utils/audioBands';
import './AudioPlayer.css';

function AudioPlayer({ track, playUrl, onStop, onNext, onPrev, hasNext, hasPrev, nextTrackUrl, onBeatSpeed, onIsPlayingChange }) {
  const { volume: contextVolume, volumeLoaded: contextVolumeLoaded, saveVolume } = useUser();
  const audioRef = useRef(null);
  const nextAudioRef = useRef(null);
  const playPromiseRef = useRef(null);
  const trackDurationRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const animationFrameRef = useRef(null);
  const mediaSourceRef = useRef(null);
  const isUserChangingVolume = useRef(false);
  const currentPlayUrlRef = useRef(null); // Текущий playUrl для проверки
  const currentTrackIdRef = useRef(null); // Текущий track.id для проверки
  const autoplayTriggeredRef = useRef(false); // Флаг для предотвращения двойного запуска автоплея
  const autoplayTimeoutRef = useRef(null); // Таймаут для автоплея
  const previousVolumeRef = useRef(null); // Предыдущая громкость перед выключением
  const lastAnalysisTimeRef = useRef(0); // Время последнего анализа для ограничения частоты до 30 раз/с
  const movementDirectionRef = useRef(1); // Направление движения анимаций (1 / -1) по преобладающей частоте
  const bandLeaderRef = useRef(null); // Полоса частот, лидирующая в текущем анализе
  const bandStreakRef = useRef(0); // Сколько анализов подряд лидирует одна и та же полоса
  const appliedLeaderRef = useRef(null); // Полоса, по которой уже сменили направление
  
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(contextVolume || 0.5);
  const [volumeLoaded, setVolumeLoaded] = useState(contextVolumeLoaded);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [bufferedPercent, setBufferedPercent] = useState(0);
  const [isBuffering, setIsBuffering] = useState(false);
  const [isNearPlayer, setIsNearPlayer] = useState(false);
  const errorTimeoutRef = useRef(null);
  const bufferingTimeoutRef = useRef(null);
  const bufferingShowTimeoutRef = useRef(null);
  const bufferingMaxTimeoutRef = useRef(null);
  const playerRef = useRef(null);

  // Направление движения анимаций (глобальная CSS-переменная).
  // Определяется преобладающей полосой частот трека.
  const setMovementDirection = useCallback((dir) => {
    if (movementDirectionRef.current === dir) return;
    movementDirectionRef.current = dir;
    document.documentElement.style.setProperty('--animation-direction', String(dir));
  }, []);

  // Предзагрузка следующего трека
  useEffect(() => {
    if (!nextTrackUrl || !hasNext) {
      if (nextAudioRef.current) {
        nextAudioRef.current.pause();
        nextAudioRef.current.src = '';
      }
      return;
    }

    if (!nextAudioRef.current) {
      nextAudioRef.current = new Audio();
      nextAudioRef.current.preload = 'auto';
    }

    nextAudioRef.current.src = nextTrackUrl;
    nextAudioRef.current.load();
    
    return () => {
      if (nextAudioRef.current) {
        nextAudioRef.current.pause();
        nextAudioRef.current.src = '';
      }
    };
  }, [nextTrackUrl, hasNext]);

  // Функция для полной остановки и очистки audio элемента (ОПТИМИЗИРОВАНА ДЛЯ БЫСТРОГО ПЕРЕКЛЮЧЕНИЯ)
  const stopAndCleanAudio = useRef((audio) => {
    if (!audio) return;
    try {
      // СРАЗУ отменяем промисы воспроизведения
      if (playPromiseRef.current) {
        playPromiseRef.current.catch(() => {});
        playPromiseRef.current = null;
      }
      // СРАЗУ останавливаем воспроизведение (синхронно, без await)
      audio.pause();
      // Сбрасываем позицию
      audio.currentTime = 0;
      // НЕ вызываем audio.load() здесь - это замедляет переключение
      // Просто останавливаем, src будет очищен в основном useEffect
    } catch (e) {
      // Игнорируем ошибки
    }
  });

  // Функция для отключения MediaElementSource.
  // Сам узел НЕ обнуляем: для одного <audio> MediaElementSource можно создать
  // только один раз, поэтому он переиспользуется между треками.
  const disconnectMediaSource = useRef(() => {
    if (mediaSourceRef.current) {
      try {
        mediaSourceRef.current.disconnect();
      } catch (e) {
        // Игнорируем ошибки
      }
    }
  });
  const analyserConnectedRef = useRef(false); // analyser -> destination подключён один раз

  // Установка источника при изменении playUrl
  useEffect(() => {
    const startTime = performance.now();
    console.log('[AUTOPLAY] useEffect: playUrl изменился', { 
      playUrl, 
      hasAudio: !!audioRef.current,
      track: track ? { id: track.id, name: track.name } : null,
      currentPlayUrl: currentPlayUrlRef.current,
      currentTrackId: currentTrackIdRef.current
    });
    
    // ВАЖНО: Проверяем только audioRef, playUrl может быть старым при переключении треков
    // Плеер должен оставаться видимым, пока есть playingTrack
    if (!audioRef.current) {
      console.log('[AUTOPLAY] Нет audioRef, пропуск', { 
        playUrl, 
        hasAudio: !!audioRef.current 
      });
      return;
    }
    
    // Если playUrl стал null
    if (!playUrl) {
      // ВАЖНО: Если track еще есть, это означает, что playUrl временно отсутствует
      // (например, при быстром переключении треков или отмене запроса)
      // В этом случае НЕ сбрасываем ref'ы, чтобы не потерять состояние
      if (track) {
        console.log('[AUTOPLAY] playUrl стал null, но track еще есть - ждем новый playUrl', { 
          playUrl, 
          hasAudio: !!audioRef.current,
          track: { id: track.id, name: track.name },
          currentPlayUrl: currentPlayUrlRef.current,
          currentTrackId: currentTrackIdRef.current
        });
        // НЕ сбрасываем ref'ы - они нужны для правильной обработки нового playUrl
        // Просто останавливаем воспроизведение
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
        setIsPlaying(false);
        if (onIsPlayingChange) onIsPlayingChange(false);
        setCurrentTime(0);
        setBufferedPercent(0);
        return;
      }
      
      // Если track тоже null, это означает, что трек был полностью остановлен
      console.log('[AUTOPLAY] playUrl стал null и track тоже null - полная очистка', { 
        playUrl, 
        hasAudio: !!audioRef.current,
        track: null
      });
      disconnectMediaSource.current();
      setIsPlaying(false);
      if (onIsPlayingChange) onIsPlayingChange(false);
      setCurrentTime(0);
      setDuration(0);
      setBufferedPercent(0);
      currentPlayUrlRef.current = null;
      currentTrackIdRef.current = null;
      return;
    }

    const audio = audioRef.current;
    
    // Функция нормализации URL для сравнения (вынесена в начало для переиспользования)
    const normalizeUrlForComparison = (url) => {
      if (!url) return '';
      try {
        const u = String(url);
        if (u.startsWith('http://') || u.startsWith('https://')) {
          try {
            const urlObj = new URL(u);
            return (urlObj.pathname + urlObj.search).toLowerCase().trim();
          } catch {
            const match = u.match(/https?:\/\/[^\/]+(\/.*)/);
            if (match) return match[1].toLowerCase().trim();
          }
        }
        return u.split('#')[0].toLowerCase().trim();
      } catch {
        return String(url).toLowerCase().trim();
      }
    };
    
    // Функция проверки, является ли URL базовым (пустым после очистки)
    const isBaseUrl = (url) => {
      if (!url) return true;
      try {
        const u = String(url);
        if (u.startsWith('http://') || u.startsWith('https://')) {
          try {
            const urlObj = new URL(u);
            const path = urlObj.pathname;
            return path === '' || path === '/';
          } catch {
            return /^https?:\/\/[^\/]+\/?$/.test(u);
          }
        }
        return false;
      } catch {
        return false;
      }
    };
    
    // Если это тот же трек, не перезагружаем
    // Проверяем как по playUrl, так и по track.id (для случая, когда playUrl еще не изменился)
    const normalizedCurrentPlayUrl = normalizeUrlForComparison(currentPlayUrlRef.current);
    const normalizedPlayUrl = normalizeUrlForComparison(playUrl);
    const audioSrc = audio.src || audio.currentSrc || '';
    const normalizedAudioSrc = normalizeUrlForComparison(audioSrc);
    
    // Получаем track_id из URL для сравнения
    const getTrackIdFromUrl = (url) => {
      if (!url) return null;
      try {
        const match = String(url).match(/track_id=(\d+)/);
        return match ? match[1] : null;
      } catch {
        return null;
      }
    };
    
    const currentTrackId = getTrackIdFromUrl(currentPlayUrlRef.current);
    const newTrackId = getTrackIdFromUrl(playUrl);
    const currentTrackIdFromTrack = track?.id ? String(track.id) : null;
    
    // Проверяем, тот же ли это трек:
    // 1. По playUrl (точное или нормализованное совпадение)
    // 2. По track.id (если есть)
    // 3. По track_id из URL
    const isSameTrack = 
      (currentPlayUrlRef.current === playUrl || normalizedCurrentPlayUrl === normalizedPlayUrl) && 
      (audio.src === playUrl || normalizedAudioSrc === normalizedPlayUrl) &&
      (currentTrackIdFromTrack === null || currentTrackIdFromTrack === String(currentTrackIdRef.current)) &&
      (newTrackId === null || currentTrackId === newTrackId);
    
    if (isSameTrack) {
      console.log('[AUTOPLAY] Тот же трек, пропуск перезагрузки', {
        currentPlayUrl: currentPlayUrlRef.current,
        playUrl,
        currentTrackId,
        newTrackId,
        currentTrackIdFromTrack,
        currentTrackIdRef: currentTrackIdRef.current,
        normalizedCurrentPlayUrl,
        normalizedPlayUrl
      });
      return;
    }
    
    // Получаем track_id из URL для сравнения (используем существующую функцию)
    const currentTrackIdFromUrl = getTrackIdFromUrl(currentPlayUrlRef.current);
    const newTrackIdFromUrl = getTrackIdFromUrl(playUrl);
    
    // Проверяем, изменился ли playUrl по track_id в URL
    const playUrlChangedByTrackId = currentPlayUrlRef.current !== null && 
                                    currentTrackIdFromUrl !== null &&
                                    newTrackIdFromUrl !== null &&
                                    currentTrackIdFromUrl !== newTrackIdFromUrl;
    
    // Проверяем, изменился ли playUrl по нормализованному сравнению (используем уже объявленные переменные)
    const playUrlChangedByNormalized = currentPlayUrlRef.current !== null &&
                                      currentPlayUrlRef.current !== playUrl &&
                                      normalizedCurrentPlayUrl !== normalizedPlayUrl;
    
    // Дополнительная проверка: если track.id уже обновлен, но playUrl еще старый (из предыдущего трека),
    // то это означает, что мы ждем новый playUrl
    const waitingForNewPlayUrl = track?.id && 
                                 currentTrackIdRef.current !== null &&
                                 String(track.id) === String(currentTrackIdRef.current) &&
                                 currentPlayUrlRef.current !== null &&
                                 currentTrackIdFromUrl !== null &&
                                 newTrackIdFromUrl !== null &&
                                 currentTrackIdFromUrl !== newTrackIdFromUrl &&
                                 newTrackIdFromUrl === String(track.id);
    
    // КРИТИЧНО: Если track.id изменился, но playUrl еще старый, то когда придет новый playUrl,
    // мы должны его распознать как новый, даже если currentPlayUrlRef.current еще содержит старый URL
    const isNewPlayUrlAfterTrackChange = 
      track?.id &&
      currentTrackIdRef.current !== null &&
      String(track.id) === String(currentTrackIdRef.current) &&
      currentPlayUrlRef.current !== null &&
      currentTrackIdFromUrl !== null &&
      newTrackIdFromUrl !== null &&
      currentTrackIdFromUrl !== newTrackIdFromUrl &&
      newTrackIdFromUrl === String(track.id);
    
    const playUrlChanged = playUrlChangedByTrackId || playUrlChangedByNormalized || waitingForNewPlayUrl || isNewPlayUrlAfterTrackChange;
    
    console.log('[AUTOPLAY] Проверка изменения playUrl', {
      playUrlChanged,
      playUrlChangedByTrackId,
      playUrlChangedByNormalized,
      waitingForNewPlayUrl,
      isNewPlayUrlAfterTrackChange,
      currentPlayUrl: currentPlayUrlRef.current,
      playUrl,
      currentTrackIdFromUrl,
      newTrackIdFromUrl,
      currentTrackId: currentTrackIdRef.current,
      trackId: track?.id
    });
    
    // СЛУЧАЙ 1: Первый трек (оба ref === null)
    if (track?.id && currentTrackIdRef.current === null && currentPlayUrlRef.current === null) {
      console.log('[AUTOPLAY] Первый трек - загружаем', {
        trackId: track.id,
        playUrl
      });
      currentTrackIdRef.current = String(track.id);
      // Продолжаем с загрузкой
    }
    // СЛУЧАЙ 2: track.id изменился
    else if (track?.id && currentTrackIdRef.current !== null && String(track.id) !== String(currentTrackIdRef.current)) {
      console.log('[AUTOPLAY] СЛУЧАЙ 2: track.id изменился', {
        currentTrackId: currentTrackIdRef.current,
        newTrackId: track.id,
        currentPlayUrl: currentPlayUrlRef.current,
        playUrl,
        currentTrackIdFromUrl,
        newTrackIdFromUrl,
        playUrlChanged,
        playUrlChangedByTrackId,
        playUrlChangedByNormalized
      });
      
      if (!playUrlChanged && currentPlayUrlRef.current !== null) {
        // playUrl еще не изменился - очищаем предыдущий трек и ждем
        console.log('[AUTOPLAY] Новый трек по track.id, но playUrl еще не изменился - очищаем предыдущий трек и ждем', {
          currentTrackId: currentTrackIdRef.current,
          newTrackId: track.id,
          currentPlayUrl: currentPlayUrlRef.current,
          playUrl,
          currentTrackIdFromUrl,
          newTrackIdFromUrl
        });
        // Обновляем currentTrackIdRef
        currentTrackIdRef.current = String(track.id);
        // Очищаем предыдущий трек
        audio.pause();
        audio.currentTime = 0;
        audio.src = '';
        try {
          audio.load();
        } catch (e) {
          console.warn('[AUTOPLAY] Ошибка при audio.load() после очистки:', e);
        }
        disconnectMediaSource.current();
        // ВАЖНО: НЕ вызываем setIsPlaying(false) здесь, чтобы плеер не закрылся
        // Плеер должен оставаться видимым, так как playingTrack уже установлен
        setCurrentTime(0);
        setBufferedPercent(0);
        autoplayTriggeredRef.current = false;
        // ВАЖНО: НЕ обновляем currentPlayUrlRef.current - оставляем старый для сравнения
        // Когда придет новый playUrl, он будет распознан как новый в СЛУЧАЕ 3
        console.log('[AUTOPLAY] Ожидание нового playUrl для трека', { trackId: track.id });
        return; // Выходим, дожидаемся изменения playUrl
      } else if (playUrlChanged) {
        // playUrl уже изменился - продолжаем с нормальной загрузкой
        console.log('[AUTOPLAY] Новый трек по track.id, playUrl уже изменился - продолжаем с загрузкой', {
          currentTrackId: currentTrackIdRef.current,
          newTrackId: track.id,
          currentPlayUrl: currentPlayUrlRef.current,
          playUrl,
          currentTrackIdFromUrl,
          newTrackIdFromUrl
        });
        // Обновляем currentTrackIdRef и продолжаем
        currentTrackIdRef.current = String(track.id);
        // Продолжаем с загрузкой
      } else {
        // Это не должно произойти, но на всякий случай
        console.warn('[AUTOPLAY] СЛУЧАЙ 2: Неожиданная ситуация', {
          currentTrackId: currentTrackIdRef.current,
          newTrackId: track.id,
          currentPlayUrl: currentPlayUrlRef.current,
          playUrl,
          playUrlChanged
        });
        currentTrackIdRef.current = String(track.id);
        // Продолжаем с загрузкой
      }
    }
    // СЛУЧАЙ 3: track.id тот же, но playUrl изменился (после очистки в СЛУЧАЕ 2)
    else if (track?.id && currentTrackIdRef.current !== null && String(track.id) === String(currentTrackIdRef.current)) {
      console.log('[AUTOPLAY] СЛУЧАЙ 3: track.id тот же, проверяем playUrl', {
        trackId: track.id,
        currentTrackId: currentTrackIdRef.current,
        currentPlayUrl: currentPlayUrlRef.current,
        playUrl,
        currentTrackIdFromUrl,
        newTrackIdFromUrl,
        playUrlChanged,
        playUrlChangedByTrackId,
        playUrlChangedByNormalized
      });
      
      // ВАЖНО: Проверяем, что playUrl действительно новый (по track_id в URL)
      // Если currentPlayUrlRef содержит старый URL с другим track_id, а новый playUrl имеет правильный track_id,
      // то это новый playUrl, который нужно загрузить
      const isNewPlayUrlForCurrentTrack = 
        currentPlayUrlRef.current !== null &&
        currentTrackIdFromUrl !== null &&
        newTrackIdFromUrl !== null &&
        currentTrackIdFromUrl !== newTrackIdFromUrl &&
        newTrackIdFromUrl === String(track.id);
      
      // КРИТИЧНО: В СЛУЧАЕ 3 мы должны загрузить новый playUrl, если он отличается от currentPlayUrlRef
      // Это происходит после очистки в СЛУЧАЕ 2, когда приходит новый playUrl
      const normalizedCurrentPlayUrlForCase3 = normalizeUrlForComparison(currentPlayUrlRef.current);
      const normalizedPlayUrlForCase3 = normalizeUrlForComparison(playUrl);
      const playUrlActuallyChanged = normalizedCurrentPlayUrlForCase3 !== normalizedPlayUrlForCase3;
      
      if (playUrlChanged || isNewPlayUrlForCurrentTrack || playUrlActuallyChanged) {
        // playUrl изменился - это новый playUrl для того же трека (после очистки в СЛУЧАЕ 2)
        console.log('[AUTOPLAY] Тот же трек по track.id, но playUrl изменился - загружаем', {
          trackId: track.id,
          currentPlayUrl: currentPlayUrlRef.current,
          playUrl,
          currentTrackIdFromUrl,
          newTrackIdFromUrl,
          isNewPlayUrlForCurrentTrack,
          playUrlActuallyChanged,
          normalizedCurrentPlayUrl: normalizedCurrentPlayUrlForCase3,
          normalizedPlayUrl: normalizedPlayUrlForCase3
        });
        // Продолжаем с загрузкой
      } else if (currentPlayUrlRef.current === null) {
        // playUrl был сброшен - загружаем
        console.log('[AUTOPLAY] Тот же трек по track.id, но playUrl был сброшен - загружаем', {
          trackId: track.id,
          playUrl
        });
        // Продолжаем с загрузкой
      } else {
        // Тот же трек и тот же playUrl - пропускаем
        console.log('[AUTOPLAY] Тот же трек и тот же playUrl - пропуск', {
          trackId: track.id,
          playUrl,
          currentPlayUrl: currentPlayUrlRef.current,
          currentTrackIdFromUrl,
          newTrackIdFromUrl,
          normalizedCurrentPlayUrl: normalizedCurrentPlayUrlForCase3,
          normalizedPlayUrl: normalizedPlayUrlForCase3
        });
        return;
      }
    }
    // СЛУЧАЙ 4: Трек был очищен (currentPlayUrlRef === null), но currentTrackIdRef уже установлен
    else if (track?.id && currentTrackIdRef.current !== null && currentPlayUrlRef.current === null) {
      console.log('[AUTOPLAY] Трек был очищен, загружаем новый playUrl', {
        trackId: track.id,
        currentTrackId: currentTrackIdRef.current,
        playUrl
      });
      // Обновляем currentTrackIdRef если нужно
      if (String(track.id) !== String(currentTrackIdRef.current)) {
        currentTrackIdRef.current = String(track.id);
      }
      // Продолжаем с загрузкой
    }

    console.log('[AUTOPLAY] Новый трек, сброс состояния', { 
      oldPlayUrl: currentPlayUrlRef.current, 
      newPlayUrl: playUrl 
    });

    // СРАЗУ сбрасываем состояние воспроизведения (до остановки audio)
    setIsPlaying(false);
    if (onIsPlayingChange) onIsPlayingChange(false);
    setCurrentTime(0);
    setBufferedPercent(0);
    autoplayTriggeredRef.current = false; // Сбрасываем флаг автоплея
    console.log('[AUTOPLAY] Флаг автоплея сброшен');
    
    // СРАЗУ отменяем промисы воспроизведения
    if (playPromiseRef.current) {
      playPromiseRef.current.catch(() => {});
      playPromiseRef.current = null;
    }
    
    // Полностью останавливаем предыдущий трек (быстро)
    stopAndCleanAudio.current(audio);
    
    // Отключаем MediaElementSource ПЕРЕД изменением src
    disconnectMediaSource.current();
    
    // КРИТИЧНО: Агрессивная очистка предыдущего трека
    // 1. Сначала останавливаем и сбрасываем позицию
    audio.pause();
    audio.currentTime = 0;
    
    // 2. Отменяем все промисы воспроизведения
    if (playPromiseRef.current) {
      playPromiseRef.current.catch(() => {});
      playPromiseRef.current = null;
    }
    
    // 3. Очищаем источник
    audio.src = '';
    
    // 4. Вызываем load() для полной очистки буфера
    try {
      audio.load();
    } catch (e) {
      console.warn('[AUTOPLAY] Ошибка при audio.load() после очистки:', e);
    }
    
    // Сбрасываем остальное состояние
    setDuration(0);
    setLoadError(false);
    
    // Сохраняем текущий playUrl ПОСЛЕ очистки, но ПЕРЕД установкой нового источника
    currentPlayUrlRef.current = playUrl;
    
    // Устанавливаем новый источник СРАЗУ синхронно (без задержек)
    if (audioRef.current && currentPlayUrlRef.current === playUrl) {
      // Дополнительная проверка - убеждаемся, что предыдущий трек очищен
      // Используем нормализацию для сравнения абсолютных и относительных URL
      const currentSrc = audioRef.current.src || audioRef.current.currentSrc || '';
      
      // Игнорируем базовые URL (это не предыдущий трек, а просто пустой src после очистки)
      if (isBaseUrl(currentSrc)) {
        // Это базовый URL, не предыдущий трек - устанавливаем новый источник без дополнительной очистки
        console.log('[AUTOPLAY] Установка нового источника (базовый URL игнорирован)', { playUrl });
        audioRef.current.preload = 'auto';
        audioRef.current.src = playUrl;
        const nonLinearVolume = linearToNonLinear(volume);
        audioRef.current.volume = nonLinearVolume;
        console.log('[AUTOPLAY] Вызов audio.load()');
        try {
          audioRef.current.load();
        } catch (e) {
          console.warn('[AUTOPLAY] Ошибка при audio.load() для нового трека:', e);
        }
      } else {
        const normalizedCurrentSrc = normalizeUrlForComparison(currentSrc);
        const normalizedPlayUrl = normalizeUrlForComparison(playUrl);
        const srcMatches = normalizedCurrentSrc === normalizedPlayUrl || 
                          (currentSrc && playUrl && (currentSrc.includes(playUrl) || playUrl.includes(currentSrc)));
        
        if (currentSrc && currentSrc !== '' && !srcMatches) {
          // Предыдущий трек все еще загружен - быстрая очистка и установка нового
          console.log('[AUTOPLAY] Предыдущий трек все еще загружен, быстрая очистка', { 
            currentSrc, 
            playUrl,
            normalizedCurrentSrc,
            normalizedPlayUrl
          });
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          audioRef.current.src = '';
          try {
            audioRef.current.load();
          } catch (e) {
            console.warn('[AUTOPLAY] Ошибка при финальной очистке:', e);
          }
          // Устанавливаем новый источник сразу после очистки (синхронно)
          console.log('[AUTOPLAY] Установка нового источника после финальной очистки', { playUrl });
          audioRef.current.preload = 'auto';
          audioRef.current.src = playUrl;
          const nonLinearVolume = linearToNonLinear(volume);
          audioRef.current.volume = nonLinearVolume;
          try {
            audioRef.current.load();
          } catch (e) {
            console.warn('[AUTOPLAY] Ошибка при audio.load() для нового трека:', e);
          }
        } else {
          // Предыдущий трек очищен или совпадает - устанавливаем новый источник сразу
          console.log('[AUTOPLAY] Установка нового источника', { playUrl });
          audioRef.current.preload = 'auto';
          audioRef.current.src = playUrl;
          const nonLinearVolume = linearToNonLinear(volume);
          audioRef.current.volume = nonLinearVolume;
          console.log('[AUTOPLAY] Вызов audio.load()');
          try {
            audioRef.current.load();
          } catch (e) {
            console.warn('[AUTOPLAY] Ошибка при audio.load() для нового трека:', e);
          }
        }
      }
    } else {
      console.log('[AUTOPLAY] Трек изменился, пропуск установки нового источника');
    }
    
    // Сохраняем последний трек в БД
    if (track) {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      if (token) {
        axios.post(`${API_URL}/api/auth/last-track`, 
          { track: track },
          { headers: { Authorization: `Bearer ${token}` } }
        ).catch(() => {});
      }
    }
    
    // Устанавливаем начальную длительность из трека
    if (track && track.duration) {
      const trackDuration = track.duration;
      const durationInSeconds = trackDuration > 100000 ? trackDuration / 1000 : trackDuration;
      if (durationInSeconds > 0 && !isNaN(durationInSeconds) && durationInSeconds < 3600) {
        setDuration(durationInSeconds);
        trackDurationRef.current = durationInSeconds;
      }
    } else {
      trackDurationRef.current = null;
    }

    // МАКСИМАЛЬНО ПРОСТАЯ И АГРЕССИВНАЯ ЛОГИКА АВТОПЛЕЯ
    const triggerAutoplay = (force = false, source = 'unknown') => {
      console.log('[AUTOPLAY] triggerAutoplay вызван', { force, source, playUrl, currentPlayUrl: currentPlayUrlRef.current, autoplayTriggered: autoplayTriggeredRef.current });
      
      // Проверяем, что автоплей еще не был запущен для этого трека
      if (autoplayTriggeredRef.current && !force) {
        console.log('[AUTOPLAY] Пропуск: автоплей уже запущен');
        return;
      }
      
      // Проверяем, что это все еще тот же трек
      if (!audioRef.current || currentPlayUrlRef.current !== playUrl) {
        console.log('[AUTOPLAY] Пропуск: трек изменился', { 
          hasAudio: !!audioRef.current, 
          currentPlayUrl: currentPlayUrlRef.current, 
          playUrl 
        });
        return;
      }
      
      // Проверяем, что audio элемент готов
      // Используем currentSrc вместо src (более надежно после load())
      const audioSrc = audioRef.current.currentSrc || audioRef.current.src || '';
      const playUrlStr = String(playUrl || '');
      
      // Нормализуем URL для сравнения (браузер может изменить формат)
      const normalizeUrl = (url) => {
        if (!url) return '';
        try {
          const u = String(url);
          // Если это абсолютный URL, извлекаем путь + query
          if (u.startsWith('http://') || u.startsWith('https://')) {
            try {
              const urlObj = new URL(u);
              // Возвращаем путь + query (без домена и протокола)
              return (urlObj.pathname + urlObj.search).toLowerCase().trim();
            } catch {
              // Если не удалось распарсить, убираем протокол и домен вручную
              const match = u.match(/https?:\/\/[^\/]+(\/.*)/);
              if (match) {
                return match[1].toLowerCase().trim();
              }
            }
          }
          // Для относительного URL просто убираем якоря и нормализуем
          return u.split('#')[0].toLowerCase().trim();
        } catch {
          return String(url).toLowerCase().trim();
        }
      };
      
      const normalizedAudioSrc = normalizeUrl(audioSrc);
      const normalizedPlayUrl = normalizeUrl(playUrlStr);
      
      // Проверяем совпадение: точное или нормализованное
      // Улучшенная проверка для абсолютных и относительных URL
      const srcMatches = audioSrc === playUrlStr || 
                        (audioSrc && playUrlStr && (
                          audioSrc.includes(playUrlStr) || 
                          playUrlStr.includes(audioSrc) ||
                          audioSrc.endsWith(playUrlStr) ||
                          playUrlStr.endsWith(audioSrc)
                        )) ||
                        (normalizedAudioSrc && normalizedPlayUrl && (
                          normalizedAudioSrc === normalizedPlayUrl ||
                          normalizedAudioSrc.endsWith(normalizedPlayUrl) ||
                          normalizedPlayUrl.endsWith(normalizedAudioSrc)
                        ));
      
      if (!srcMatches) {
        // Если src пустой, но readyState >= 1, все равно пробуем (может быть CORS или другой формат)
        if (!audioSrc && audioRef.current.readyState < 1) {
          console.log('[AUTOPLAY] Пропуск: src не установлен и readyState < 1', { 
            src: audioRef.current.src,
            currentSrc: audioRef.current.currentSrc,
            audioSrc,
            playUrl: playUrlStr,
            readyState: audioRef.current.readyState
          });
          return;
        }
        
        // Если force=true, пропускаем проверку src
        if (!force) {
          console.log('[AUTOPLAY] Пропуск: src не совпадает', { 
            src: audioRef.current.src,
            currentSrc: audioRef.current.currentSrc,
            audioSrc,
            playUrl: playUrlStr,
            normalizedAudioSrc,
            normalizedPlayUrl,
            srcMatches,
            force
          });
          return;
        } else {
          console.log('[AUTOPLAY] FORCE: пропускаем проверку src', { 
            src: audioRef.current.src,
            currentSrc: audioRef.current.currentSrc,
            audioSrc,
            playUrl: playUrlStr
          });
        }
      } else {
        console.log('[AUTOPLAY] src совпадает', { 
          src: audioRef.current.src,
          currentSrc: audioRef.current.currentSrc,
          audioSrc,
          playUrl: playUrlStr 
        });
      }
      
      // Требуем readyState >= 2 (HAVE_CURRENT_DATA) для автоплея
      // Если force=true, пробуем даже если readyState < 2, но не раньше чем через 1.5 секунды
      const readyState = audioRef.current.readyState;
      const readyStateNames = ['HAVE_NOTHING', 'HAVE_METADATA', 'HAVE_CURRENT_DATA', 'HAVE_FUTURE_DATA', 'HAVE_ENOUGH_DATA'];
      console.log('[AUTOPLAY] Проверка готовности', { 
        readyState, 
        readyStateName: readyStateNames[readyState] || 'UNKNOWN',
        force,
        bufferedLength: audioRef.current.buffered.length 
      });
      
      // Минимальное требование: readyState >= 2 (HAVE_CURRENT_DATA) для нормального автоплея
      // Или readyState >= 1 (HAVE_METADATA) если force=true или есть буфер
      // Или readyState >= 3 (HAVE_FUTURE_DATA) - это уже достаточно для воспроизведения
      const bufferedLength = audioRef.current.buffered.length;
      const hasBufferedData = bufferedLength > 0;
      
      if (!force && readyState < 2) {
        // Если readyState >= 3 (HAVE_FUTURE_DATA), это уже достаточно для воспроизведения
        if (readyState >= 3) {
          console.log('[AUTOPLAY] readyState >= 3 (HAVE_FUTURE_DATA), достаточно для воспроизведения', { 
            readyState, 
            readyStateName: readyStateNames[readyState] || 'UNKNOWN',
            force,
            srcMatches 
          });
          // Продолжаем, не возвращаемся
        } 
        // Если readyState >= 1 и есть буфер, тоже можно запускать
        else if (readyState >= 1 && hasBufferedData) {
          console.log('[AUTOPLAY] readyState >= 1 (HAVE_METADATA) и есть буфер, запускаем', { 
            readyState, 
            readyStateName: readyStateNames[readyState] || 'UNKNOWN',
            bufferedLength,
            force,
            srcMatches 
          });
          // Продолжаем, не возвращаемся
        } else {
          console.log('[AUTOPLAY] Пропуск: readyState < 2 (HAVE_CURRENT_DATA) и не force', { 
            readyState, 
            readyStateName: readyStateNames[readyState] || 'UNKNOWN',
            hasBufferedData,
            bufferedLength,
            force,
            srcMatches 
          });
          return;
        }
      }
      
      if (force && readyState < 1) {
        console.log('[AUTOPLAY] Пропуск: readyState < 1 (HAVE_METADATA) даже с force', { 
          readyState, 
          readyStateName: readyStateNames[readyState] || 'UNKNOWN',
          force 
        });
        return;
      }
      
      // Отмечаем, что автоплей запущен (сразу, чтобы не запустить дважды)
      autoplayTriggeredRef.current = true;
      console.log('[AUTOPLAY] Запуск воспроизведения...', { 
        readyState, 
        readyStateName: readyStateNames[readyState] || 'UNKNOWN',
        force,
        srcMatches 
      });
      
      // Очищаем таймауты автоплея если были
      if (autoplayTimeoutRef.current) {
        clearTimeout(autoplayTimeoutRef.current);
        autoplayTimeoutRef.current = null;
      }
      
      // Очищаем таймеры буферизации
      if (bufferingShowTimeoutRef.current) {
        clearTimeout(bufferingShowTimeoutRef.current);
        bufferingShowTimeoutRef.current = null;
      }
      if (bufferingMaxTimeoutRef.current) {
        clearTimeout(bufferingMaxTimeoutRef.current);
        bufferingMaxTimeoutRef.current = null;
      }
      setIsBuffering(false);
      
      // НЕ устанавливаем isPlaying здесь - дождемся события 'playing'
      // setIsPlaying(true);
      // if (onIsPlayingChange) onIsPlayingChange(true);
      console.log('[AUTOPLAY] Вызов audio.play()...');
      const playPromise = audioRef.current.play();
      playPromiseRef.current = playPromise;
      
      if (playPromise !== undefined) {
        console.log('[AUTOPLAY] play() вернул Promise');
        playPromise.then(() => {
          console.log('[AUTOPLAY] Promise resolved - воспроизведение началось');
          // НЕ устанавливаем isPlaying здесь - дождемся события 'playing'
          // Событие 'playing' - самое надежное для установки isPlaying
          if (audioRef.current && currentPlayUrlRef.current !== playUrl) {
            setIsPlaying(false);
            if (onIsPlayingChange) onIsPlayingChange(false);
            autoplayTriggeredRef.current = false; // Сбрасываем флаг если трек сменился
            console.log('[AUTOPLAY] Трек изменился после запуска, сброс флага');
          }
          playPromiseRef.current = null;
        }).catch(err => {
          console.error('[AUTOPLAY] Ошибка воспроизведения:', err);
          console.log('[AUTOPLAY] Сброс флага для повторной попытки');
          autoplayTriggeredRef.current = false;
          setIsBuffering(false);
          setIsPlaying(false);
          if (onIsPlayingChange) onIsPlayingChange(false);
          playPromiseRef.current = null;
        });
      } else {
        console.log('[AUTOPLAY] play() не вернул Promise (старый браузер?)');
        // НЕ устанавливаем isPlaying здесь - дождемся события 'playing'
        // Для старых браузеров тоже ждем события 'playing'
        if (audioRef.current && currentPlayUrlRef.current !== playUrl) {
          autoplayTriggeredRef.current = false;
          setIsPlaying(false);
          if (onIsPlayingChange) onIsPlayingChange(false);
          console.log('[AUTOPLAY] Трек изменился, сброс флага');
        }
        playPromiseRef.current = null;
      }
    };

    // Обработчик progress - основной для автоплея (срабатывает чаще всего)
    const handleProgressAutoplay = () => {
      // Убрано избыточное логирование - событие progress слишком частое
      // console.log('[AUTOPLAY] Событие: progress', { 
      //   readyState: audioRef.current?.readyState,
      //   bufferedLength: audioRef.current?.buffered.length 
      // });
      if (!autoplayTriggeredRef.current && currentPlayUrlRef.current === playUrl) {
        triggerAutoplay(false, 'progress');
      }
    };

    // Обработчик canplay - когда audio готов к воспроизведению
    const handleCanPlay = () => {
      console.log('[AUTOPLAY] Событие: canplay', { 
        readyState: audioRef.current?.readyState 
      });
      if (!autoplayTriggeredRef.current && currentPlayUrlRef.current === playUrl) {
        triggerAutoplay(false, 'canplay');
      }
    };

    // Обработчик loadeddata - когда данные загружены
    const handleLoadedData = () => {
      console.log('[AUTOPLAY] Событие: loadeddata', { 
        readyState: audioRef.current?.readyState 
      });
      if (!autoplayTriggeredRef.current && currentPlayUrlRef.current === playUrl) {
        triggerAutoplay(false, 'loadeddata');
      }
    };

    // Обработчик canplaythrough - когда все готово
    const handleCanPlayThrough = () => {
      console.log('[AUTOPLAY] Событие: canplaythrough', { 
        readyState: audioRef.current?.readyState 
      });
      if (!autoplayTriggeredRef.current && currentPlayUrlRef.current === playUrl) {
        triggerAutoplay(false, 'canplaythrough');
      }
    };

    // Обработчик loadedmetadata - когда метаданные загружены (самый ранний)
    const handleLoadedMetadata = () => {
      console.log('[AUTOPLAY] Событие: loadedmetadata', { 
        readyState: audioRef.current?.readyState 
      });
      if (!autoplayTriggeredRef.current && currentPlayUrlRef.current === playUrl) {
        triggerAutoplay(false, 'loadedmetadata');
      }
    };

    // Автоплей запускается по событиям готовности (loadedmetadata, loadeddata, canplay, canplaythrough)
    // Также добавляем принудительный запуск через 2 секунды для надежности
    // Очищаем старые таймауты (если были)
    if (autoplayTimeoutRef.current) {
      clearTimeout(autoplayTimeoutRef.current);
      autoplayTimeoutRef.current = null;
    }
    
    console.log('[AUTOPLAY] Ожидание событий готовности для автоплея', { playUrl });
    
    // Принудительный запуск через 2 секунды, если автоплей еще не сработал
    autoplayTimeoutRef.current = setTimeout(() => {
      if (!autoplayTriggeredRef.current && currentPlayUrlRef.current === playUrl && audioRef.current) {
        console.log('[AUTOPLAY] Принудительный запуск через 2 секунды', { 
          readyState: audioRef.current.readyState,
          autoplayTriggered: autoplayTriggeredRef.current,
          currentPlayUrl: currentPlayUrlRef.current,
          playUrl,
          hasAudio: !!audioRef.current
        });
        triggerAutoplay(true, 'timeout-2000ms-force'); // force=true - пробуем даже если readyState < 2
      } else {
        console.log('[AUTOPLAY] Принудительный запуск пропущен', { 
          autoplayTriggered: autoplayTriggeredRef.current,
          currentPlayUrl: currentPlayUrlRef.current,
          playUrl,
          hasAudio: !!audioRef.current
        });
      }
      autoplayTimeoutRef.current = null;
    }, 2000);

    // Удаляем старые обработчики
    audio.removeEventListener('canplaythrough', handleCanPlayThrough);
    audio.removeEventListener('canplay', handleCanPlay);
    audio.removeEventListener('loadeddata', handleLoadedData);
    audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
    audio.removeEventListener('progress', handleProgressAutoplay);
    
    // Добавляем новые обработчики (в порядке приоритета)
    audio.addEventListener('loadedmetadata', handleLoadedMetadata); // Самый ранний
    audio.addEventListener('loadeddata', handleLoadedData);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('canplaythrough', handleCanPlayThrough);
    audio.addEventListener('progress', handleProgressAutoplay); // Самый частый

    return () => {
      // Очистка при смене трека
      audio.removeEventListener('canplaythrough', handleCanPlayThrough);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('loadeddata', handleLoadedData);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('progress', handleProgressAutoplay);
      
      // Очищаем таймаут автоплея
      if (autoplayTimeoutRef.current) {
        clearTimeout(autoplayTimeoutRef.current);
        autoplayTimeoutRef.current = null;
      }
      
      // Отменяем промисы
      if (playPromiseRef.current) {
        playPromiseRef.current.catch(() => {});
        playPromiseRef.current = null;
      }
      
      // Останавливаем воспроизведение
      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        } catch (e) {}
      }
      
      setIsPlaying(false);
      if (onIsPlayingChange) onIsPlayingChange(false);
      setCurrentTime(0);
      setBufferedPercent(0);
    };
  }, [playUrl, track, onIsPlayingChange]); // Добавили onIsPlayingChange в зависимости

  // Инициализация Web Audio API для анализа битов
  useEffect(() => {
    if (!playUrl || !audioRef.current) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      disconnectMediaSource.current();
      if (onBeatSpeed) {
        onBeatSpeed(1.0);
      }
      bandLeaderRef.current = null;
      bandStreakRef.current = 0;
      appliedLeaderRef.current = null;
      setMovementDirection(1);
      return;
    }
    
    // НЕ проверяем isPlaying здесь - анализ должен работать даже на паузе
    // чтобы быстро реагировать на возобновление воспроизведения

    const audio = audioRef.current;
    
    // Создаем AudioContext если его нет
    if (!audioContextRef.current) {
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        audioContextRef.current = new AudioContext();
      } catch (e) {
        console.warn('Web Audio API не поддерживается:', e);
        return;
      }
    }

    const audioContext = audioContextRef.current;
    
    // Создаем AnalyserNode
    if (!analyserRef.current) {
      analyserRef.current = audioContext.createAnalyser();
      analyserRef.current.fftSize = 256;
      analyserRef.current.smoothingTimeConstant = 0.8;
      dataArrayRef.current = new Uint8Array(analyserRef.current.frequencyBinCount);
    }

    const analyser = analyserRef.current;
    const dataArray = dataArrayRef.current;

    // Отключаем старый MediaElementSource перед созданием нового
    // (не отключаем аудиограф заранее — иначе при несовпадении src пропадёт звук)
    
    // audio.src всегда возвращает абсолютный URL, а playUrl может быть относительным
    // (/api/yandex/stream?track_id=...), поэтому сравниваем приведённые пути.
    const toComparableUrl = (url) => {
      if (!url) return '';
      try {
        const parsed = new URL(url, window.location.origin);
        return `${parsed.pathname}${parsed.search}`;
      } catch {
        return String(url);
      }
    };

    // Подключаем анализатор, только если источник соответствует текущему треку
    const comparableAudioSrc = toComparableUrl(audio.src || audio.currentSrc);
    const comparablePlayUrl = toComparableUrl(playUrl);
    if (comparableAudioSrc && comparableAudioSrc === comparablePlayUrl) {
      try {
        if (audioContext.state === 'suspended') {
          audioContext.resume();
        }
        // MediaElementSource создаётся один раз на элемент и переиспользуется
        if (!mediaSourceRef.current) {
          mediaSourceRef.current = audioContext.createMediaElementSource(audio);
        }
        // Переподключаем чисто, чтобы не копить дубли связей
        try {
          mediaSourceRef.current.disconnect();
        } catch (e) {
          // Игнорируем
        }
        mediaSourceRef.current.connect(analyser);
        if (!analyserConnectedRef.current) {
          analyser.connect(audioContext.destination);
          analyserConnectedRef.current = true;
        }
      } catch (e) {
        // Если не удалось подключить, просто пропускаем анализ битов
        console.warn('Ошибка подключения Web Audio API:', e);
      }
    }

    // Функция анализа битов с ограничением частоты до 30 раз/с
    const ANALYSIS_INTERVAL = 1000 / 30; // 30 раз в секунду (33.33ms между анализами)
    // Сколько анализов подряд полоса должна лидировать, чтобы сменить направление
    const LEADER_STREAK = 6;
    
    const analyzeBeats = (currentTime) => {
      // Проверяем, что все компоненты готовы
      if (!analyser || !dataArray || !audioRef.current || currentPlayUrlRef.current !== playUrl) {
        if (onBeatSpeed) {
          onBeatSpeed(1.0);
        }
        setMovementDirection(1);
        // Останавливаем анализ если нет трека
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        return;
      }

      // Проверяем, что audio элемент играет - ОСТАНАВЛИВАЕМ на паузе
      if (!isPlaying || audioRef.current.paused) {
        if (onBeatSpeed) {
          onBeatSpeed(1.0);
        }
        setMovementDirection(1);
        // ОСТАНАВЛИВАЕМ анализ на паузе для экономии CPU
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        return;
      }

      // Ограничиваем частоту анализа до 30 раз/с
      const timeSinceLastAnalysis = currentTime - lastAnalysisTimeRef.current;
      if (timeSinceLastAnalysis < ANALYSIS_INTERVAL) {
        animationFrameRef.current = requestAnimationFrame(analyzeBeats);
        return;
      }
      
      lastAnalysisTimeRef.current = currentTime;

      analyser.getByteFrequencyData(dataArray);

      const bassStart = 0;
      const bassEnd = Math.min(10, dataArray.length);
      let bassMax = 0;

      for (let i = bassStart; i < bassEnd; i++) {
        if (dataArray[i] > bassMax) {
          bassMax = dataArray[i];
        }
      }

      const normalizedBass = Math.min(1, bassMax / 255);
      const speedMultiplier = 1.0 + (normalizedBass * 0.2);
      
      // Средняя энергия по полосам спектра — ищем преобладающую частоту
      const { leader } = pickDominantBand(dataArray);

      // Гистерезис: реагируем только на устойчиво лидирующую полосу
      if (leader === bandLeaderRef.current) {
        bandStreakRef.current += 1;
      } else {
        bandLeaderRef.current = leader;
        bandStreakRef.current = 1;
      }

      if (bandStreakRef.current >= LEADER_STREAK && leader !== appliedLeaderRef.current) {
        appliedLeaderRef.current = leader;
        // Низкие частоты — движение вперёд, высокие — назад,
        // средние — направление не меняем
        const direction = directionForBand(leader);
        if (direction !== null) {
          setMovementDirection(direction);
        }
      }
      
      if (onBeatSpeed) {
        onBeatSpeed(speedMultiplier);
        // Логирование для отладки (можно убрать позже)
        if (speedMultiplier > 1.05) {
          console.log('[BEAT] Обнаружен бит:', { speedMultiplier, normalizedBass, bassMax, leader });
        }
      }

      animationFrameRef.current = requestAnimationFrame(analyzeBeats);
    };

    // Запускаем анализ только если трек играет
    if (mediaSourceRef.current && isPlaying && audioRef.current && !audioRef.current.paused) {
      lastAnalysisTimeRef.current = performance.now();
      animationFrameRef.current = requestAnimationFrame(analyzeBeats);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      // Узел MediaElementSource не отключаем: он переиспользуется между треками,
      // иначе после смены состояния пропадёт звук.
    };
  }, [playUrl, isPlaying, onBeatSpeed, setMovementDirection]);

  // Отслеживание времени воспроизведения и состояния
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => {
      if (!audioRef.current) return;
      // Проверяем, что это все еще актуальный трек
      if (currentPlayUrlRef.current !== playUrl) {
        return; // Трек сменился, не обновляем время
      }
      setCurrentTime(audio.currentTime);
      
      if (audio.buffered.length > 0 && audio.duration > 0) {
        const bufferedEnd = audio.buffered.end(audio.buffered.length - 1);
        const bufferedPercent = (bufferedEnd / audio.duration) * 100;
        setBufferedPercent(Math.min(100, bufferedPercent));
        
        if (isPlaying && bufferedEnd - audio.currentTime < 3 && audio.readyState < 3) {
          if (bufferingShowTimeoutRef.current) {
            clearTimeout(bufferingShowTimeoutRef.current);
          }
          if (bufferingMaxTimeoutRef.current) {
            clearTimeout(bufferingMaxTimeoutRef.current);
          }
          
          bufferingShowTimeoutRef.current = setTimeout(() => {
            setIsBuffering(true);
            bufferingMaxTimeoutRef.current = setTimeout(() => {
              setIsBuffering(false);
            }, 3000);
          }, 100);
        } else if (bufferedEnd - audio.currentTime >= 3) {
          if (bufferingShowTimeoutRef.current) {
            clearTimeout(bufferingShowTimeoutRef.current);
            bufferingShowTimeoutRef.current = null;
          }
          if (bufferingMaxTimeoutRef.current) {
            clearTimeout(bufferingMaxTimeoutRef.current);
            bufferingMaxTimeoutRef.current = null;
          }
          setIsBuffering(false);
        }
      }
    };
    
    const updateDuration = () => {
      if (trackDurationRef.current && trackDurationRef.current > 0) {
        return;
      }
      if (audio.duration && !isNaN(audio.duration) && audio.duration > 0 && audio.duration < 3600) {
        setDuration(audio.duration);
      }
    };
    
    const handleProgress = () => {
      if (audio.buffered.length > 0 && audio.duration > 0) {
        const bufferedEnd = audio.buffered.end(audio.buffered.length - 1);
        const bufferedPercent = (bufferedEnd / audio.duration) * 100;
        setBufferedPercent(Math.min(100, bufferedPercent));
      }
    };
    
    const handleWaiting = () => {
      if (bufferingShowTimeoutRef.current) {
        clearTimeout(bufferingShowTimeoutRef.current);
      }
      if (bufferingMaxTimeoutRef.current) {
        clearTimeout(bufferingMaxTimeoutRef.current);
      }
      
      bufferingShowTimeoutRef.current = setTimeout(() => {
        setIsBuffering(true);
        bufferingMaxTimeoutRef.current = setTimeout(() => {
          setIsBuffering(false);
        }, 3000);
      }, 100);
    };
    
    const handlePlaying = () => {
      // КРИТИЧНО: Событие 'playing' срабатывает когда трек действительно начал играть
      // Это самое надежное место для обновления состояния
      // Проверяем что это действительно наш трек
      if (audioRef.current && currentPlayUrlRef.current === playUrl) {
        setIsPlaying(true);
        if (onIsPlayingChange) onIsPlayingChange(true);
        console.log('[AUDIO] Событие playing - трек начал играть');
      }
      if (bufferingShowTimeoutRef.current) {
        clearTimeout(bufferingShowTimeoutRef.current);
        bufferingShowTimeoutRef.current = null;
      }
      if (bufferingMaxTimeoutRef.current) {
        clearTimeout(bufferingMaxTimeoutRef.current);
        bufferingMaxTimeoutRef.current = null;
      }
      setIsBuffering(false);
    };
    
    const handlePlay = () => {
      // Событие 'play' срабатывает при вызове play(), но не гарантирует что трек играет
      // Проверяем что это наш трек
      if (audioRef.current && currentPlayUrlRef.current === playUrl) {
        setIsPlaying(true);
        if (onIsPlayingChange) onIsPlayingChange(true);
        console.log('[AUDIO] Событие play');
      }
      setLoadError(false);
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = null;
      }
    };
    
    const handlePause = () => {
      // Событие 'pause' - проверяем что это наш трек и действительно пауза
      if (audioRef.current && currentPlayUrlRef.current === playUrl && audioRef.current.paused) {
        setIsPlaying(false);
        if (onIsPlayingChange) onIsPlayingChange(false);
        const pauseTime = performance.now();
        console.log('[AUDIO] Событие pause', { 
          currentPlayUrl: currentPlayUrlRef.current,
          playUrl,
          timestamp: pauseTime
        });
      }
    };
    
    const handleEnded = () => {
      setIsPlaying(false);
      if (onIsPlayingChange) onIsPlayingChange(false);
      setCurrentTime(0);
      if (onNext && hasNext) {
        setTimeout(() => {
          onNext();
        }, 500);
      } else if (onStop) {
        onStop();
      }
    };
    
    const handleCanPlay = () => {
      setLoadError(false);
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = null;
      }
      if (bufferingShowTimeoutRef.current) {
        clearTimeout(bufferingShowTimeoutRef.current);
        bufferingShowTimeoutRef.current = null;
      }
      if (bufferingMaxTimeoutRef.current) {
        clearTimeout(bufferingMaxTimeoutRef.current);
        bufferingMaxTimeoutRef.current = null;
      }
      setIsBuffering(false);
    };
    
    const handleError = () => {
      setLoadError(true);
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      if (bufferingShowTimeoutRef.current) {
        clearTimeout(bufferingShowTimeoutRef.current);
        bufferingShowTimeoutRef.current = null;
      }
      if (bufferingMaxTimeoutRef.current) {
        clearTimeout(bufferingMaxTimeoutRef.current);
        bufferingMaxTimeoutRef.current = null;
      }
      setIsBuffering(false);
      errorTimeoutRef.current = setTimeout(() => {
        if (audio.error && !isPlaying) {
          setLoadError(false);
          if (onStop) onStop();
        }
      }, 5000);
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('progress', handleProgress);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('playing', handlePlaying);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('progress', handleProgress);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('playing', handlePlaying);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('error', handleError);
    };
  }, [isPlaying, onStop, track, playUrl, hasNext, onNext, onIsPlayingChange]);

  // Преобразование нелинейного значения громкости в линейное
  const linearToNonLinear = (linearValue) => {
    return linearValue * linearValue;
  };

  // Синхронизация громкости из контекста
  useEffect(() => {
    if (isUserChangingVolume.current) {
      return;
    }
    
    if (contextVolumeLoaded && contextVolume !== undefined) {
      setVolume(contextVolume);
      setVolumeLoaded(true);
      // Сохраняем contextVolume как предыдущую громкость, если она не была сохранена
      if (previousVolumeRef.current === null && contextVolume > 0) {
        previousVolumeRef.current = contextVolume;
      }
      if (audioRef.current) {
        audioRef.current.volume = linearToNonLinear(contextVolume);
      }
    } else if (!contextVolumeLoaded) {
      setVolumeLoaded(false);
    }
  }, [contextVolume, contextVolumeLoaded]);
  
  // Сохранение громкости через контекст
  useEffect(() => {
    if (!volumeLoaded || !saveVolume) return;
    
    const timeoutId = setTimeout(() => {
      saveVolume(volume);
    }, 1000);
    
    return () => clearTimeout(timeoutId);
  }, [volume, volumeLoaded, saveVolume]);

  // Применение громкости к audio элементу (только если audio элемент загружен)
  useEffect(() => {
    if (audioRef.current && audioRef.current.src) {
      const nonLinearVolume = linearToNonLinear(volume);
      audioRef.current.volume = nonLinearVolume;
      if (audioRef.current.volume !== nonLinearVolume) {
        audioRef.current.volume = nonLinearVolume;
      }
    }
  }, [volume]);

  // Синхронизация isPlaying с контекстом
  useEffect(() => {
    if (onIsPlayingChange) {
      onIsPlayingChange(isPlaying);
    }
  }, [isPlaying, onIsPlayingChange]);
  
  // Применение громкости после загрузки из базы данных
  useEffect(() => {
    if (volumeLoaded && audioRef.current) {
      audioRef.current.volume = linearToNonLinear(volume);
    }
  }, [volumeLoaded, volume]);

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      disconnectMediaSource.current();
    };
  }, []);

  // Обработка приближения курсора к плееру
  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;

    const handleMouseMove = (e) => {
      const rect = player.getBoundingClientRect();
      const playerHeight = rect.height;
      const threshold = playerHeight * 0.1; // 10% от высоты
      const distanceFromTop = e.clientY - rect.top;
      const distanceFromBottom = rect.bottom - e.clientY;
      const isNear = distanceFromTop < threshold || distanceFromBottom < threshold;
      setIsNearPlayer(isNear);
    };

    const handleMouseLeave = () => {
      setIsNearPlayer(false);
    };

    player.addEventListener('mousemove', handleMouseMove);
    player.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      player.removeEventListener('mousemove', handleMouseMove);
      player.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    
    // Проверяем реальное состояние audio элемента
    const actuallyPlaying = !audioRef.current.paused && audioRef.current.currentTime > 0 && !audioRef.current.ended;
    
    if (actuallyPlaying || isPlaying) {
      // Пауза
      audioRef.current.pause();
      setIsPlaying(false);
      if (onIsPlayingChange) onIsPlayingChange(false);
      if (playPromiseRef.current) {
        playPromiseRef.current.catch(() => {});
        playPromiseRef.current = null;
      }
    } else {
      // Воспроизведение
      if (playPromiseRef.current) {
        playPromiseRef.current.catch(() => {});
        playPromiseRef.current = null;
      }
      setIsPlaying(true);
      if (onIsPlayingChange) onIsPlayingChange(true);
      const playPromise = audioRef.current.play();
      playPromiseRef.current = playPromise;
      if (playPromise !== undefined) {
        playPromise.then(() => {
          // После успешного play() проверяем что это наш трек
          if (audioRef.current && currentPlayUrlRef.current === playUrl) {
            setIsPlaying(true);
            if (onIsPlayingChange) onIsPlayingChange(true);
            console.log('[PLAYPAUSE] Воспроизведение запущено');
          } else {
            setIsPlaying(false);
            if (onIsPlayingChange) onIsPlayingChange(false);
          }
          playPromiseRef.current = null;
        }).catch(err => {
          console.error('Ошибка воспроизведения:', err);
          setIsPlaying(false);
          if (onIsPlayingChange) onIsPlayingChange(false);
          playPromiseRef.current = null;
        });
      } else {
        // Старый браузер без Promise
        if (audioRef.current && currentPlayUrlRef.current === playUrl) {
          setIsPlaying(true);
          if (onIsPlayingChange) onIsPlayingChange(true);
          console.log('[PLAYPAUSE] Воспроизведение запущено (без Promise)');
        } else {
          setIsPlaying(false);
          if (onIsPlayingChange) onIsPlayingChange(false);
        }
        playPromiseRef.current = null;
      }
    }
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;
    const newTime = parseFloat(e.target.value);
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    isUserChangingVolume.current = true;
    setVolume(newVolume);
    
    if (audioRef.current) {
      const nonLinearVolume = linearToNonLinear(newVolume);
      audioRef.current.volume = nonLinearVolume;
      if (audioRef.current.volume !== nonLinearVolume) {
        audioRef.current.volume = nonLinearVolume;
      }
    }
    
    if (volumeLoaded && saveVolume) {
      saveVolume(newVolume);
    }
    setTimeout(() => {
      isUserChangingVolume.current = false;
    }, 100);
  };

  const handleOpenLink = () => {
    const externalUrl = track.externalUrl || track.url;
    if (externalUrl) {
      window.open(externalUrl, '_blank');
    }
  };

  const handleDownload = async () => {
    if (!track.id && !track.url) return;
    
    try {
      await downloadTrack(track);
    } catch (error) {
      console.error('Ошибка скачивания:', error);
      alert(`Ошибка при скачивании трека: ${error.message}`);
    }
  };

  if (!track || !playUrl) return null;

  const trackImage = track.albumImage || track.thumbnail;
  const trackTitle = track.name || track.title || 'Без названия';
  const trackArtist = track.artist || track.channelTitle || 'Неизвестный исполнитель';
  const trackSource = track.source === 'yandex' ? 'Яндекс.Музыка' : track.source === 'spotify' ? 'Spotify' : track.source === 'soundcloud' ? 'SoundCloud' : 'YouTube';
  const hasExternalLink = track.externalUrl || track.url;
  const canDownload = track.id || track.url;

  return (
    <>
      <audio 
        ref={audioRef}
        preload="auto"
        crossOrigin="anonymous"
      />
      <div 
        ref={playerRef}
        className={`audio-player ${isPlaying ? 'playing' : ''} ${isNearPlayer ? 'near-cursor' : ''}`}
      >
        <div className="player-content">
          <div className="player-track-info">
            <div className="player-track-image">
              {trackImage ? (
                <img src={trackImage} alt={trackTitle} />
              ) : (
                <div className="player-no-image"><MusicIcon /></div>
              )}
            </div>
            <div className="player-track-details">
              <div className="player-track-text">
                <h4>{trackTitle}</h4>
                <p>{trackArtist} • {trackSource}</p>
              </div>
              <div className="player-actions">
                {hasExternalLink && (
                  <button
                    className="player-action-btn"
                    onClick={handleOpenLink}
                    title="Открыть ссылку на трек"
                  >
                    <LinkIcon />
                  </button>
                )}
                {canDownload && (
                  <button
                    className="player-action-btn"
                    onClick={handleDownload}
                    title="Скачать трек"
                  >
                    <DownloadIcon />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="player-controls">
            {loadError && !isPlaying && (
              <div className="player-status-message">
                <div className="player-status-bg"></div>
                <span className="player-status-text">Загрузка...</span>
              </div>
            )}
            {isBuffering && isPlaying && (
              <div className="player-status-message">
                <div className="player-status-bg"></div>
                <span className="player-status-text">Буферизация...</span>
              </div>
            )}
            {hasPrev && onPrev && (
              <button
                className="player-nav-btn"
                onClick={onPrev}
                title="Предыдущий трек"
              >
                <PrevIcon />
              </button>
            )}
            <button
              className="player-play-pause-btn"
              onClick={handlePlayPause}
              title={isPlaying ? 'Пауза' : 'Воспроизведение'}
              disabled={loadError && !isPlaying}
            >
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </button>
            {hasNext && onNext && (
              <button
                className="player-nav-btn"
                onClick={onNext}
                title="Следующий трек"
              >
                <NextIcon />
              </button>
            )}
            
            <div className="player-progress">
              <span className="player-time">{formatDuration(currentTime)}</span>
              <div className="player-seek-wrapper">
                <input
                  type="range"
                  min="0"
                  max={duration || 0}
                  value={currentTime}
                  onChange={handleSeek}
                  className="player-seek"
                  style={{
                    background: `linear-gradient(to right, 
                      rgba(102, 126, 234, 0.8) 0%, 
                      rgba(102, 126, 234, 0.8) ${(currentTime / duration) * 100 || 0}%, 
                      rgba(102, 126, 234, 0.3) ${(currentTime / duration) * 100 || 0}%, 
                      rgba(102, 126, 234, 0.3) ${bufferedPercent}%, 
                      rgba(255, 255, 255, 0.1) ${bufferedPercent}%, 
                      rgba(255, 255, 255, 0.1) 100%)`
                  }}
                />
              </div>
              <span className="player-time">{formatDuration(duration || 0)}</span>
            </div>
          </div>

          <div className="player-volume">
            <button
              className="player-volume-btn"
              onClick={() => {
                let newVolume;
                if (volume > 0) {
                  // Выключаем звук - сохраняем текущую громкость
                  previousVolumeRef.current = volume;
                  newVolume = 0;
                } else {
                  // Включаем звук - восстанавливаем сохранённую громкость
                  // Приоритет: сохранённая громкость > contextVolume > localStorage > 0.5
                  if (previousVolumeRef.current !== null && previousVolumeRef.current > 0) {
                    newVolume = previousVolumeRef.current;
                  } else if (contextVolume !== undefined && contextVolume > 0) {
                    newVolume = contextVolume;
                  } else {
                    // Пытаемся загрузить из localStorage
                    const savedVolume = localStorage.getItem('player_volume');
                    if (savedVolume) {
                      const parsedVolume = parseFloat(savedVolume);
                      if (!isNaN(parsedVolume) && parsedVolume > 0 && parsedVolume <= 1) {
                        newVolume = parsedVolume;
                      } else {
                        newVolume = 0.5; // Значение по умолчанию
                      }
                    } else {
                      newVolume = 0.5; // Значение по умолчанию
                    }
                  }
                }
                isUserChangingVolume.current = true;
                setVolume(newVolume);
                if (volumeLoaded && saveVolume) {
                  saveVolume(newVolume);
                }
                setTimeout(() => {
                  isUserChangingVolume.current = false;
                }, 100);
              }}
              title={volume > 0 ? 'Выключить звук' : 'Включить звук'}
            >
              {volume === 0 ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16.5 12C16.5 10.23 15.48 8.71 14 7.97V10.18L16.45 12.63C16.48 12.43 16.5 12.22 16.5 12Z" fill="currentColor"/>
                  <path d="M19 12C19 12.94 18.8 13.82 18.46 14.64L19.97 16.15C20.63 14.91 21 13.5 21 12C21 7.72 18.01 4.14 14 3.23V5.29C16.89 6.15 19 8.83 19 12ZM4.27 3L3 4.27L7.73 9H3V15H7L12 20V13.27L16.25 17.53C15.58 18.04 14.83 18.46 14 18.7V20.77C15.38 20.45 16.63 19.82 17.68 18.96L19.73 21L21 19.73L12 10.73L4.27 3ZM12 4L9.91 6.09L12 8.18V4Z" fill="currentColor"/>
                </svg>
              ) : linearToNonLinear(volume) < 0.5 ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18.5 12C18.5 10.23 17.48 8.71 16 7.97V10.18L18.45 12.63C18.48 12.43 18.5 12.22 18.5 12Z" fill="currentColor"/>
                  <path d="M5 9V15H9L14 20V4L9 9H5Z" fill="currentColor"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 9V15H7L12 20V4L7 9H3ZM16.5 12C16.5 10.23 15.48 8.71 14 7.97V16.02C15.48 15.29 16.5 13.77 16.5 12ZM14 3.23V5.29C16.89 6.15 19 8.83 19 12C19 15.17 16.89 17.85 14 18.71V20.77C18.01 19.86 21 16.28 21 12C21 7.72 18.01 4.14 14 3.23Z" fill="currentColor"/>
                </svg>
              )}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={handleVolumeChange}
              className="player-volume-slider"
            />
            <span className="player-volume-value">{Math.round(volume * 100)}%</span>
          </div>
        </div>
      </div>
    </>
  );
}

export default memo(AudioPlayer);
